import "./i18n";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import CssBaseline from "@mui/material/CssBaseline";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ErrorBoundary } from "react-error-boundary";
import {
  ActiveFarmProvider,
  AlertProvider,
  ApiProvider,
  ConfigProvider,
  UserProvider,
  useConfigContext,
} from "@/contexts";
import { ThemeProvider } from "@mui/material/styles";
import { BrowserRouter as Router } from "react-router-dom";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import ErrorFallback from "@/components/common/ErrorFallback";
import { Auth0ProviderWithConfig } from "@/components/providers/Auth0ProviderWithConfig";
import PageLoading from "@/pages/loading";
import theme from "@/config/theme";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import "moment/locale/en-gb";
import "moment/locale/pt";

// UI / body — Geist
import "@fontsource/geist-sans/400.css";
import "@fontsource/geist-sans/500.css";
import "@fontsource/geist-sans/600.css";
import "@fontsource/geist-sans/700.css";
// Display / headlines — Nunito
import "@fontsource/nunito/400.css";
import "@fontsource/nunito/500.css";
import "@fontsource/nunito/600.css";
import "@fontsource/nunito/700.css";
import "@fontsource/nunito/800.css";
// Data / mono — JetBrains Mono
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";

// eslint-disable-next-line react-refresh/only-export-components
function AppBootstrap() {
  const { loading, error, retryConfig } = useConfigContext();
  const { i18n, t } = useTranslation();
  const momentLocale =
    SUPPORTED_LANGUAGES.find(({ code }) => code === i18n.language)
      ?.momentLocale || "en-gb";

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 600 }}>
          <Alert
            severity="error"
            action={
              error.canRetry && (
                <Button color="inherit" size="small" onClick={retryConfig}>
                  {t("retry")}
                </Button>
              )
            }
          >
            <Typography variant="h6" component="div" sx={{ mb: 1 }}>
              {error.message}
            </Typography>
            <Typography variant="body1">{t("retryContactSupport")}</Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return <PageLoading loadingText={t("loadingAppConfig")} />;
  }

  return (
    <Auth0ProviderWithConfig>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={momentLocale}
        >
          <AlertProvider>
            <ApiProvider>
              <UserProvider>
                <ActiveFarmProvider>
                  <App />
                </ActiveFarmProvider>
              </UserProvider>
            </ApiProvider>
          </AlertProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </Auth0ProviderWithConfig>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <ErrorBoundary
    FallbackComponent={ErrorFallback}
    onReset={() => window.location.assign("/")}
  >
    <ConfigProvider>
      <Router>
        <AppBootstrap />
      </Router>
    </ConfigProvider>
  </ErrorBoundary>
);

reportWebVitals();
